import type { KyInstance } from 'ky';

import { ensureError, formatString, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { TemplateQuery } from './types/template-query.type';
import type { Template } from './types/template.type';

/**
 * Retrieves a template along with its batched templates based on the provided query.
 *
 * Either `id` or `name` must be specified in the query to fetch the template.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param query - The query object containing either the template `id` or `name`.
 * @returns A promise resolving to the operation result containing the template and its batched templates.
 * @throws Will throw an error if neither `id` nor `name` is provided, or if the request fails.
 */
export const getTemplateWithBatches = async (instance: KyInstance, query: TemplateQuery) => {
  try {
    const { id, name } = query;

    if (isNotDefined(id) && isNotDefined(name)) {
      throw new Error('Either id or name must be provided to get a template.');
    }

    return await instance
      .get<OperationResultWithData<Template[]>>(
        formatString(Uri.template.getTemplateWithBatches, id?.toString() ?? name ?? '')
      )
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
